import { Search, RotateCw } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import 'react-toggle/style.css'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import ClockIcon from '../../../components/atoms/Icons/ClockIcon'
import api from '../../../config/AxiosInstance'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const SelectStyles = {
  control: (base, state) => ({
    ...base,
    height: 45,
    minHeight: 45,
    borderRadius: '10px',
    borderColor: state.isFocused ? 'var(--primary-orange)' : '#e9ecef',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(248, 157, 29, 0.1)' : 'none',
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    fontSize: '0.9rem'
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    height: '45px',
    padding: '0 12px'
  }),
  input: (provided, state) => ({
    ...provided,
    margin: '0px'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#495057'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--primary-orange)'
      : state.isFocused
        ? '#fff5e6'
        : null,
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer'
  })
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const SlotDetailPage = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const [warehouses, setWarehouses] = useState([])
  const [slots, setSlots] = useState([])
  const [selectedWarehouse, setSelectedWarehouse] = useState(state?.id || '')
  const [selectedType, setSelectedType] = useState(state?.type || '')
  const [searchQuery, setSearchQuery] = useState(state?.q || '')
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery)

  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })
  const { per_page, current_page } = meta

  const refSrcWarehouse = useRef()
  const refSrcType = useRef()

  const fetchWarehouses = useCallback(async () => {
    try {
      const res = await api.get(
        'master/warehouses/all?is_parent=true&forUser=1'
      )
      const options = [{ value: '', label: 'All Warehouses' }]
      res.data.data.forEach((w) => {
        options.push({ value: w.id, label: w.warehouse_name, raw: w })
      })
      setWarehouses(options)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }, [])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery)
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [searchQuery])

  const fetchSlot = useCallback(async () => {
    try {
      setIsLoading(true)
      const params = {
        warehouse_id: selectedWarehouse,
        per_page,
        page: current_page,
        q: debouncedQuery,
        type: selectedType === '' ? null : selectedType
      }
      const res = await api.get('master/slot-detail', { params })
      const { metadata, data } = res.data
      setSlots(data)
      setMeta(metadata)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setIsLoading(false)
    }
  }, [per_page, current_page, selectedWarehouse, debouncedQuery, selectedType])

  useEffect(() => {
    fetchWarehouses()
  }, [fetchWarehouses])

  useEffect(() => {
    if (selectedWarehouse) {
      fetchSlot()
    } else {
      setSlots([])
      setMeta((prev) => ({ ...prev, total_row: 0, total_page: 0 }))
    }
  }, [fetchSlot, selectedWarehouse])

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({ ...prevMeta, current_page: data.selected + 1 }))
    window.scrollTo(0, 0)
  }

  const handleReset = () => {
    setSelectedWarehouse('')
    setSelectedType('')
    setSearchQuery('')
    setDebouncedQuery('')
    setMeta((prev) => ({ ...prev, current_page: 1 }))
    refSrcWarehouse.current?.clearValue()
    refSrcType.current?.clearValue()
  }

  const typeOptions = [
    { value: '', label: 'All Types' },
    { value: 'rack', label: 'Rack' },
    { value: 'ground', label: 'Ground' }
  ]

  return (
    <Container fluid className="px-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Slot Detail</h3>
        </Col>
        <Col className="text-end">
          <Button
            variant="light"
            className="text-secondary fw-bold rounded px-3 border"
            onClick={handleReset}
            style={{ fontSize: '0.85rem' }}
          >
            <RotateCw size={16} className="me-2" />
            Reset Filters
          </Button>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Warehouse</Form.Label>
              <Select
                ref={refSrcWarehouse}
                options={warehouses}
                value={warehouses.find((w) => w.value === selectedWarehouse)}
                onChange={(e) => {
                  setSelectedWarehouse(e?.value || '')
                  setMeta((prev) => ({ ...prev, current_page: 1 }))
                }}
                styles={SelectStyles}
                placeholder="Select Warehouse"
                isClearable
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Type</Form.Label>
              <Select
                ref={refSrcType}
                options={typeOptions}
                value={typeOptions.find((t) => t.value === selectedType)}
                onChange={(e) => {
                  setSelectedType(e?.value || '')
                  setMeta((prev) => ({ ...prev, current_page: 1 }))
                }}
                styles={SelectStyles}
                placeholder="Select Type"
                isClearable
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Search</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Slot/Rack Name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                  disabled={!selectedWarehouse}
                />
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Row>
        <Col md={12}>
          <Table
            hover
            responsive
            className="shadow-sm mb-5 bg-white"
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
              fontSize: '0.9rem'
            }}
          >
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Slot Name
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Rack Name
                </th>
                <th
                  className="p-2 align-middle text-end"
                  style={tableHeaderStyle}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={3}>
                    <Loader />
                  </td>
                </tr>
              )}
              {!isLoading && slots.length === 0 && <RowEmpty colSpan={3} />}
              {!isLoading &&
                slots?.map((slot) => (
                  <tr
                    key={slot.id}
                    style={{ borderBottom: '1px solid #f0f0f0' }}
                  >
                    <td className="p-2 align-middle">{slot.slot}</td>
                    <td className="p-2 align-middle">{slot?.rack}</td>
                    <td className="p-2 align-middle text-end">
                      <TableAction>
                        <button
                          className="btn btn-secondary rounded"
                          style={{
                            width: '32px',
                            height: '32px',
                            padding: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onClick={() =>
                            navigate(`/master/slot-details/detail/${slot.id}`, {
                              state: {
                                id: selectedWarehouse,
                                warehouse_id: warehouses.find(
                                  (w) => w.value === selectedWarehouse
                                )?.raw?.warehouse_id,
                                type: selectedType,
                                code: slot.slot
                              }
                            })
                          }
                          title="View Detail"
                        >
                          <ClockIcon />
                        </button>
                      </TableAction>
                    </td>
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="p-3 border-top-0">
                  <TablePagination
                    meta={meta}
                    handlePageChange={handlePageChange}
                    handleOnChangeMeta={handleOnChangeMeta}
                  />
                </td>
              </tr>
            </tfoot>
          </Table>
        </Col>
      </Row>
    </Container>
  )
}

export default SlotDetailPage
